import { useEffect, useState, useCallback } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api, errMsg } from "@/lib/api";
import { useDash } from "./ctx";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { CalendarDays, Plus, Sparkles, Settings2, List, CalendarRange, History, ChevronLeft, ChevronRight } from "lucide-react";
import { EventsCalendar, monthKey } from "@/components/EventsCalendar";
import { EventEditor, emptyForm, formFrom, to12h } from "@/components/events/EventEditor";
import { EventCard } from "@/components/events/EventCard";
import { RsvpDialog, AnnounceDialog } from "@/components/events/EventDialogs";
import { ModuleSettingsDialog } from "@/components/ModuleSettingsDialog";
import { V } from "@/lib/vocab";

const PAGE = 20;

export default function Events() {
  const { tenantId, role, tenant } = useDash();
  const [view, setView] = useState("upcoming");            // upcoming | past | calendar
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(1);
  const [month, setMonth] = useState(monthKey(new Date()));
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [rsvpEvent, setRsvpEvent] = useState(null);
  const [announceEvent, setAnnounceEvent] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const load = useCallback(() => {
    if (!tenantId) return;
    const params = view === "calendar" ? { month, page_size: 200 } : { scope: view, page, page_size: PAGE };
    if (category) params.category = category;
    api.get(`/tenants/${tenantId}/events`, { params }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e, "Couldn't load events")));
  }, [tenantId, view, month, page, category]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [view, category, tenantId]);

  const items = data?.items || [];
  const canManage = !!data?.can_manage;
  const settings = data?.settings;

  const openNew = () => setForm(emptyForm(settings));
  const openEdit = (e) => { if (!canManage) return; setForm(formFrom(e)); };
  const save = async () => {
    setSaving(true);
    try {
      const body = {
        title: form.title.trim(), description: form.description, date: form.date || null, time: form.time ? to12h(form.time) : null,
        end_time: form.end_time ? to12h(form.end_time) : null, category: form.category, location: form.location, host_name: form.host_name,
        host_contact: form.host_contact, capacity: form.capacity === "" ? null : Number(form.capacity), rsvp_enabled: !!form.rsvp_enabled,
        flyer_path: form.flyer_path || null, apply_to: form.apply_to, announce: !!form.announce,
        recurrence: !form.id && form.recurring ? form.recurrence : null,
      };
      if (form.id) {
        const r = await api.put(`/tenants/${tenantId}/events/${form.id}`, body);
        toast.success(form.apply_to === "series" ? `Series updated · ${r.data.touched} upcoming date${r.data.touched === 1 ? "" : "s"}` : "Event updated");
      } else {
        const r = await api.post(`/tenants/${tenantId}/events`, body);
        const n = r.data.items?.length || 1;
        toast.success(n > 1 ? `Series created — ${n} dates on the calendar${form.announce ? " · announced" : ""}` : `Event created${form.announce ? " and announced" : ""}`);
      }
      setForm(null);
      load();
    } catch (e) { toast.error(errMsg(e, "Couldn't save")); }
    finally { setSaving(false); }
  };
  const remove = (e) => setConfirm({
    title: e.series_id ? `Delete “${e.title}”?` : `Delete “${e.title}”?`,
    body: e.series_id ? "This date only, or this and every future date in the series? Past dates stay on record either way." : `${V.many("person")} will no longer hear about it, and its RSVPs go with it.`,
    series: !!e.series_id,
    run: async (scope) => {
      try { const r = await api.delete(`/tenants/${tenantId}/events/${e.id}`, { params: { apply_to: scope } }); toast.success(scope === "series" ? `Removed ${r.data.removed} upcoming date${r.data.removed === 1 ? "" : "s"}` : "Event deleted"); load(); }
      catch (err) { toast.error(errMsg(err, "Couldn't delete")); }
    },
  });

  const pages = data?.pages || 1;
  const Tab = ({ v, icon: Icon, label }) => (
    <button data-testid={`events-tab-${v}`} onClick={() => setView(v)} aria-pressed={view === v}
      className={`rounded-full px-3.5 py-1.5 text-sm font-semibold inline-flex items-center gap-1.5 transition-colors ${view === v ? "bg-ink text-white" : "bg-white border border-sand-200 text-ink/70 hover:border-ink"}`}><Icon className="h-4 w-4" /> {label}</button>
  );

  return (
    <div className="space-y-6" data-testid="events-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <PageTitle title={<><CalendarDays className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> Events</>} about={<>Everything on the {V.lc("org")} calendar. {V.many("people")} can ask the concierge “what's on this weekend?”, RSVP by text, phone or the portal, and get reminders{settings?.reminders_enabled === false ? " (reminders are off)" : ""}.</>} />
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            {role !== "board_member" && (
              <button data-testid="events-settings-btn" onClick={() => setSettingsOpen(true)} title="Events module settings" className="h-10 w-10 rounded-full border border-sand-200 bg-white text-ink/60 hover:text-ink hover:border-ink flex items-center justify-center"><Settings2 className="h-4 w-4" /></button>
            )}
            <button data-testid="add-event-btn" onClick={openNew} className="rounded-full bg-redline-500 text-white font-semibold px-4 py-2.5 flex items-center gap-2 hover:bg-redline-600 transition-colors"><Plus className="h-4 w-4" strokeWidth={2.5} /> New event</button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Tab v="upcoming" icon={List} label="Upcoming" />
        <Tab v="calendar" icon={CalendarRange} label="Calendar" />
        <Tab v="past" icon={History} label="Past" />
        <div className="hidden sm:block w-px h-6 bg-sand-200 mx-1" />
        <select data-testid="events-category-filter" value={category} onChange={(e) => setCategory(e.target.value)} className="rounded-full border border-sand-200 bg-white px-3 py-1.5 text-sm outline-none focus:border-ink">
          <option value="">All themes</option>
          {(data?.categories || []).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
      </div>

      {view === "calendar" ? (
        <EventsCalendar month={month} items={items} today={data?.today || new Date().toISOString().slice(0, 10)} onMonth={setMonth}
          onOpen={(e) => (canManage ? openEdit(e) : setRsvpEvent(e))} />
      ) : (
        <div className="space-y-2">
          {data && items.length === 0 && (
            <div className="bg-white rounded-2xl border border-sand-200 p-8 text-center text-sm text-muted-foreground flex flex-col items-center gap-3" data-testid="events-empty">
              <div className="h-12 w-12 rounded-2xl bg-redline-50 flex items-center justify-center"><Sparkles className="h-6 w-6 text-redline-500" /></div>
              {view === "past" ? "No past events on record yet." : category ? "Nothing on the calendar for that theme." : `Nothing on the calendar yet — add your first event and the concierge will start telling ${V.lcs("people")} about it.`}
            </div>
          )}
          {items.map((e) => (
            <EventCard key={e.id} e={e} canManage={canManage} isPast={view === "past"} onEdit={openEdit} onDelete={remove}
              onRsvps={setRsvpEvent} onAnnounce={setAnnounceEvent} />
          ))}
          {pages > 1 && (
            <div className="flex items-center justify-end gap-2 text-xs text-muted-foreground" data-testid="events-pager">
              <span>{(page - 1) * PAGE + 1}–{Math.min(page * PAGE, data.total)} of {data.total}</span>
              <button data-testid="events-prev" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="h-7 w-7 rounded-lg border border-sand-200 bg-white flex items-center justify-center disabled:opacity-40"><ChevronLeft className="h-3.5 w-3.5" /></button>
              <span data-testid="events-page">{page}/{pages}</span>
              <button data-testid="events-next" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="h-7 w-7 rounded-lg border border-sand-200 bg-white flex items-center justify-center disabled:opacity-40"><ChevronRight className="h-3.5 w-3.5" /></button>
            </div>
          )}
        </div>
      )}

      <EventEditor open={!!form} form={form} setForm={setForm} categories={data?.categories} settings={settings} smsReady={!!data?.sms_ready}
        saving={saving} onSave={save} onClose={() => setForm(null)} tenantId={tenantId}
        seriesText={form?.series_id ? items.find((i) => i.id === form.id)?.recurrence_text : ""} />
      <RsvpDialog event={rsvpEvent} tenantId={tenantId} canManage={canManage} onClose={() => setRsvpEvent(null)} onChanged={load} />
      <AnnounceDialog event={announceEvent} tenantId={tenantId} settings={settings} smsReady={!!data?.sms_ready} onClose={() => setAnnounceEvent(null)} onDone={load} />
      <ModuleSettingsDialog module={settingsOpen ? "events" : null} tenantId={tenantId} tenant={tenant} onClose={() => { setSettingsOpen(false); load(); }} onSaved={() => load()} />

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent className="max-w-sm" data-testid="event-confirm">
          <DialogHeader><DialogTitle className="font-heading">{confirm?.title}</DialogTitle><DialogDescription>{confirm?.body}</DialogDescription></DialogHeader>
          <DialogFooter className="flex-wrap gap-2">
            <button onClick={() => setConfirm(null)} className="rounded-full font-semibold px-5 py-2.5 border border-sand-200 hover:bg-sand-50">Cancel</button>
            {confirm?.series && <button data-testid="event-confirm-series" onClick={async () => { const fn = confirm.run; setConfirm(null); await fn("series"); }} className="rounded-full font-semibold px-5 py-2.5 border border-danger-500 text-danger-600 hover:bg-danger-50">All future dates</button>}
            <button data-testid="event-confirm-ok" onClick={async () => { const fn = confirm.run; setConfirm(null); await fn("this"); }} className="rounded-full font-semibold px-6 py-2.5 text-white bg-danger-500 hover:bg-danger-600">{confirm?.series ? "Just this date" : "Delete"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
